import WizardIntegration = ToroEnums.WizardIntegration;
import WidgetType = ToroEnums.WidgetType;

import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { finalize, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashUserInfo } from '../../../../../api/dash-user/models/dash-user-info.model';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DashUserPreferences } from '../../../../../api/dash-user/models/dash-user-preferences.model';
import { ToroEnums } from '../../../../../common/enumerations/toro.enums';
import { TurfProManagerService } from '../../../../../api/turf-pro/turf-pro-manager.service';
import { WizardSettings } from '../../models/wizard-settings.model';

@UntilDestroy()
@Component({
    selector: 'toro-turf-pro-integration',
    templateUrl: './turf-pro-integration.component.html',
    styleUrls: ['./turf-pro-integration.component.less']
})
export class TurfProIntegrationComponent implements OnInit {
    @HostBinding('class') class = 'wiz-integration-component turf-pro-integration';

    @Output() canConnect = new EventEmitter<boolean>();
    @Output() configured = new EventEmitter<WizardIntegration>();
    @Output() settingsChange = new EventEmitter<WizardSettings>();

    @Input() settings: WizardSettings;

    readonly stepsCount = 1;

    isBusy = false;
    showError = false;
    spinnerText = 'CASE_SENSITIVE.VERIFYING_API_TOKEN'
    previousTurfProApiToken: string = null;
    turfProApiToken: string;

    private elements: any;
    private userPrefs: DashUserPreferences;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private broadcastService: BroadcastService,
                private dashUserManager: DashUserManagerService,
                private turfProManager: TurfProManagerService,
    ) { }

    ngOnInit(): void {
        this.broadcastService.setupDoConnect
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.verifyConfiguration();
            });

        // We want to modify the behavior of the p-inputNumber component to allow us to assess the controls
        // value after each key press. To do that, we will search the dom for our unique container component
        // and then find out "common" p-inputNumber class element. There will only be one in our container.
        // Once we have our element, we will assign the keyup event handler.
        const container = document.getElementsByClassName('wic-perry-weather-info');
        this.elements = container[0].getElementsByClassName('perry-weather-integration-key');

        if (this.elements.length != 1) throw new Error('There should be one and only one input element here.');
        this.elements[0].addEventListener('keyup', this.keyUp.bind(this))

        if (this.settings && this.settings.turfProApiToken != null) {
            this.turfProApiToken = this.settings.turfProApiToken;
            this.canConnect.next(true);
        }
    }

    ngOnDestroy() {
        this.elements[0].removeEventListener('keyup', this.keyUp.bind(this))
        this.canConnect.emit(false);
    }

    // =========================================================================================================================================================
    // Event Handler
    // =========================================================================================================================================================

    private keyUp(event) {
        this.canConnect.emit(event.target.value.length > 60 && event.target.value.includes('_'));
    }

    onPasteApiKey(event: ClipboardEvent) {
        const pastedText = event?.clipboardData?.getData('text');
        console.log(pastedText && pastedText.length > 60 && pastedText.includes('_'))

        this.canConnect.emit(pastedText && pastedText.length > 60 && pastedText.includes('_'));
    }

    onCutApiKey(event: ClipboardEvent) {
        const pastedText = event?.clipboardData?.getData('text');
        this.canConnect.emit(pastedText && pastedText.length > 60 && pastedText.includes('_'));
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private verifyConfiguration() {
        this.showError = false;
        this.isBusy = true;

        /**
         * We don't pass the Turf Pro API Token to the API when we make a call. Instead, the API extracts the PAT directrly from the user preferences
         * with the API Service. To support this behavior, we need to write the new token - even though it may be invalid - to the user preferences before
         * we make the API call to verify the token.
         */

        // Fetch existing user preferences.
        this.dashUserManager.getDashUserInfo()
            .pipe(
                switchMap((user: DashUserInfo) => {
                    this.userPrefs = user.preferences;
                    this.previousTurfProApiToken = this.userPrefs.turfProApiToken;

                    this.userPrefs.turfProApiToken = this.turfProApiToken;
                    return this.dashUserManager.updateDashUserPreferences(this.userPrefs);
                }),
                switchMap(() => this.turfProManager.getRobots()),
                finalize(() => this.isBusy = false)
            )
            .subscribe({
                next: () => {
                    this.broadcastService.setupToggleWidgetState.next({ type: WidgetType.TurfProAssetStatus, isEnabled: true });
                    this.configured.next(WizardIntegration.TurfPro);

                    // Notify Maya Widget. This will only matter if the Maya widget is already being displayed.
                    this.broadcastService.setupTurfProApiTokenChanged.next(this.turfProApiToken);
                },
                error: (err) => {
                    const error = err?.error?.Error_1[0];
                    if (error && error.toLowerCase().includes('error retrieving data')) {
                        this.showError = true;

                        // If there was a previous api token, set it back to the previous token, otherwise set to null.
                        this.userPrefs.turfProApiToken = this.previousTurfProApiToken ?? null;
                        this.dashUserManager.updateDashUserPreferences(this.userPrefs).subscribe();
                    }
                }
            })

    }
}
